import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useTranslations } from '@/hooks/useTranslations';
import { settingLabel } from '@/lib/status-labels';
import { useForm } from '@inertiajs/react';
import { Paperclip, X } from 'lucide-react';
import { useRef, useState } from 'react';
import type { CalculationRequest, CalculationRequestOptions } from '../types';

interface CompleteCalculationModalProps {
    request: CalculationRequest;
    options: CalculationRequestOptions;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

/**
 * Records the calculated amount — the figure the whole calculated-versus-sold
 * report is built on — plus the workload fields and the calculation files.
 */
export function CompleteCalculationModal({ request, options, open, onOpenChange }: CompleteCalculationModalProps) {
    const { t } = useTranslations();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [files, setFiles] = useState<File[]>([]);

    const { data, setData, post, processing, errors, reset, transform } = useForm({
        calculated_amount: request.calculated_amount ? String(request.calculated_amount) : '',
        hours_spent: request.hours_spent ? String(request.hours_spent) : '',
        complexity: request.complexity ?? '',
        completion_notes: request.completion_notes ?? '',
    });

    const submit = () => {
        // Files live in local state rather than the form, so they are merged in at
        // request time.
        transform((payload) => ({ ...payload, attachments: files }));

        post(`/calculation-requests/${request.id}/complete`, {
            forceFormData: true,
            onSuccess: () => {
                reset();
                setFiles([]);
                onOpenChange(false);
            },
        });
    };

    return (
        <ResponsiveModal open={open} onOpenChange={onOpenChange} variant="sheet">
            <ResponsiveModalContent size="lg">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('calculation_requests.complete.title', 'Record calculation')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t('calculation_requests.complete.description', 'Enter the calculated value.').replace(
                            ':number',
                            request.request_number ?? '',
                        )}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <ResponsiveModalBody className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="calculated_amount">{t('calculation_requests.complete.calculated_amount', 'Calculated amount')}</Label>
                        <Input
                            id="calculated_amount"
                            type="number"
                            step="0.01"
                            min="0"
                            value={data.calculated_amount}
                            onChange={(event) => setData('calculated_amount', event.target.value)}
                        />
                        <p className="text-muted-foreground text-xs">{t('calculation_requests.complete.calculated_amount_hint', '')}</p>
                        {errors.calculated_amount ? <p className="text-destructive text-xs">{errors.calculated_amount}</p> : null}
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                            <Label htmlFor="hours_spent">{t('calculation_requests.complete.hours_spent', 'Hours spent')}</Label>
                            <Input
                                id="hours_spent"
                                type="number"
                                step="0.25"
                                min="0"
                                value={data.hours_spent}
                                onChange={(event) => setData('hours_spent', event.target.value)}
                            />
                            {errors.hours_spent ? <p className="text-destructive text-xs">{errors.hours_spent}</p> : null}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="complexity">{t('calculation_requests.complete.complexity', 'Complexity')}</Label>
                            <Select value={data.complexity} onValueChange={(value) => setData('complexity', value)}>
                                <SelectTrigger id="complexity">
                                    <SelectValue placeholder={t('calculation_requests.complete.complexity_placeholder', 'Select complexity')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {options.complexities.map((complexity) => (
                                        <SelectItem key={complexity.value} value={complexity.value}>
                                            {settingLabel(t, 'calculation_request_complexities', complexity.value) || complexity.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {errors.complexity ? <p className="text-destructive text-xs">{errors.complexity}</p> : null}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="completion_notes">{t('calculation_requests.complete.completion_notes', 'Notes')}</Label>
                        <Textarea
                            id="completion_notes"
                            rows={3}
                            value={data.completion_notes}
                            onChange={(event) => setData('completion_notes', event.target.value)}
                            placeholder={t('calculation_requests.complete.completion_notes_placeholder', '')}
                        />
                        {errors.completion_notes ? <p className="text-destructive text-xs">{errors.completion_notes}</p> : null}
                    </div>

                    <div className="space-y-2">
                        <Label>{t('calculation_requests.complete.attachments', 'Calculation files')}</Label>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            className="hidden"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.dwg,.dxf,.zip"
                            onChange={(event) => setFiles((current) => [...current, ...Array.from(event.target.files ?? [])].slice(0, 10))}
                        />
                        <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                            <Paperclip className="mr-2 h-4 w-4" />
                            {t('calculation_requests.attachments.upload', 'Upload files')}
                        </Button>
                        <p className="text-muted-foreground text-xs">{t('calculation_requests.complete.attachments_hint', '')}</p>

                        {files.length > 0 ? (
                            <ul className="space-y-1.5">
                                {files.map((file, index) => (
                                    <li
                                        key={`${file.name}-${index}`}
                                        className="flex items-center justify-between gap-2 rounded-md border px-3 py-1.5"
                                    >
                                        <span className="truncate text-sm">{file.name}</span>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setFiles((current) => current.filter((_, i) => i !== index))}
                                        >
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </li>
                                ))}
                            </ul>
                        ) : null}
                    </div>
                </ResponsiveModalBody>

                <ResponsiveModalFooter>
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                        {t('calculation_requests.complete.cancel', 'Cancel')}
                    </Button>
                    <Button type="button" onClick={submit} disabled={processing || !data.calculated_amount}>
                        {t('calculation_requests.complete.submit', 'Record calculation')}
                    </Button>
                </ResponsiveModalFooter>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
